//FIXME: change these to date-fns functions

import { formatIntakeForm } from "@/app/directories/checkout/components/intake/IntakeForm";
import { createAndWriteIntakePdf } from "@/app/directories/checkout/components/intake/intakeHelper";
import { translateIntakeForm } from "@/app/directories/companion/components_v2/AppointmentForm/hooks/useIntakeForm";
import { generateIntakeFormPdfPC } from "@/app/directories/companion/components_v2/IntakeModalPdf";
import { datadogLogs } from "@datadog/browser-logs";
import type {
  IntakeFormSavedData,
  PolymorphicSavedResponse,
} from "@frontend/components/intake/Intake";
import { getRetrievableIntakeResponsesByIds } from "@frontend/lib/actions/actions";
import type { RosterSlot } from "@frontend/lib/graphql/generated-gql";
import { LanguageCode } from "@frontend/lib/graphql/generated-gql";
import type {
  AppointmentSchema,
  SlotsSchema,
} from "@frontend/lib/schemas/appointmentSchema";
import type { ConsumerData } from "@frontend/lib/schemas/clientSchema";
import {
  differenceInMinutes,
  format,
  isSameYear,
  isToday,
  isTomorrow,
  parse,
  parseISO,
} from "date-fns";

export function formatRelativeDate(isoDateString: string): string {
  const date = new Date(isoDateString);
  const now = new Date();

  if (isToday(date)) {
    return "Today";
  }

  if (isTomorrow(date)) {
    return "Tomorrow";
  }
  const formatStr = isSameYear(date, now)
    ? "EEEE, MMMM do"
    : "EEEE, MMMM do, yyyy";
  return format(date, formatStr);
}

export const slotIsPartiallyPaid = (slot: SlotsSchema) => {
  return slot.procedures.some((procedure) =>
    procedure.charges?.some((charge) => charge.transactionId),
  );
};

export const appointmentIsPaid = (appointment: AppointmentSchema) => {
  const procedures = appointment.slots.flatMap((slot) => slot.procedures);
  return (
    procedures.every((procedure) => !!procedure.charges?.length) &&
    (procedures.every((p) => p.charges?.every((c) => c.transactionId)) ||
      procedures.every((procedure) => procedure.price === 0)) // FIXME remove this when charges are cleaned up
  );
};

export const appointmentIsPartiallyPaid = (appointment: AppointmentSchema) => {
  return slotsArePartiallyPaid(appointment.slots);
};

export const slotsArePartiallyPaid = (slots: SlotsSchema[] | RosterSlot[]) => {
  const procedures = slots.flatMap((slot) => slot.procedures);
  const chargesWithTransactions = procedures.flatMap((procedure) =>
    (procedure.charges || []).filter((charge) => charge?.transactionId),
  );
  return chargesWithTransactions.length > 0;
};

export const calculateTimeDifference = (dateStr: string): string => {
  const givenDate = parseISO(dateStr);
  const currentTime = new Date();

  const diffInDays = differenceInMinutes(currentTime, givenDate);
  const totalDays = Math.floor(diffInDays / (24 * 60));
  const totalHours = Math.floor(diffInDays / 60);
  const totalMinutes = diffInDays;

  let result = "";

  if (totalDays > 0) {
    result += `${totalDays} day${totalDays === 1 ? "" : "s"} ago`;
  } else if (totalHours > 0) {
    result += `${totalHours} ${totalHours === 1 ? "hr" : "hrs"}`;
    if (totalMinutes % 60 > 0) {
      result += ` ${totalMinutes % 60} ${
        totalMinutes % 60 === 1 ? "min" : "mins"
      }`;
    }
    result += " late";
  } else if (totalMinutes > 0) {
    result += `${totalMinutes} ${totalMinutes === 1 ? "min" : "mins"} late`;
  } else {
    result = "Just now";
  }

  return result;
};

export const formatTimeSlot = (startTime: string, endTime: string) => {
  const today = new Date(); // arbitrary date to parse the time
  const start = parse(startTime, "h:mm a", today);
  const end = parse(endTime, "h:mm a", today);

  const formatTime = (date: Date, includePeriod: boolean) => {
    const hasMinutes = date.getMinutes() !== 0;
    const formatStr = hasMinutes
      ? includePeriod
        ? "h:mma"
        : "h:mm"
      : includePeriod
        ? "ha"
        : "h";
    return format(date, formatStr).toLowerCase();
  };
  const samePeriod = format(start, "a") === format(end, "a"); // omit period if same ex. 12 - 2pm
  return `${formatTime(start, !samePeriod)} - ${formatTime(end, true)}`;
};

const intakePdfPreProcessing = async (
  intakeFormId: number,
  appointmentId: string,
  consumerId: string,
  consumerInfo: ConsumerData,
  formName: string,
) => {
  const intakeForms = await getIntakeFormsByFormIds([intakeFormId], true);
  const formattedIntakeForms = formatIntakeFormsWithQuestions(
    intakeForms,
    "en" as LanguageCode,
  );
  const intakeResponses = await getIntakeFormResponse(appointmentId, [
    intakeFormId,
  ]);

  const safeIntakeForms = formattedIntakeForms ?? [];
  const safeIntakeResponse = intakeResponses ?? [];
  const intakeData = safeIntakeForms.map((form) => ({
    form: form,
    response: safeIntakeResponse.find((x) => x.intakeFormId === form.id),
  }));

  const questionIds =
    intakeData
      .at(0)
      ?.form.intakeFormToQuestions.map((q) => q.intakeQuestion.id) ?? [];

  const retrievableIntakeResponses = await getRetrievableIntakeResponsesByIds(
    questionIds,
    consumerId,
  );
  if (retrievableIntakeResponses) {
    const parsedIntakeResponses = retrievableIntakeResponses.reduce(
      (accum, x) => {
        accum[x.intakeQuestionId] = {
          response: x.response,
        };
        return accum;
      },
      {} as Record<number, PolymorphicSavedResponse>,
    );

    const {
      intakeFormQuestions,
      tempHiddenQuestions,
      defaultValues,
      sectionedQuestions,
    } = formatIntakeForm(intakeData, parsedIntakeResponses);

    const timeCreated = intakeData.at(0)?.response?.createdAt;

    return {
      intakeFormQuestions,
      tempHiddenQuestions,
      defaultValues,
      sectionedQuestions,
      timeCreated,
    };
  }
};

export const resubmitIntakePdf = async (
  intakeFormId: number,
  appointmentId: string,
  consumerId: string,
  consumerInfo: ConsumerData,
  formName: string,
  orgId: number,
  practiceId: number | undefined,
) => {
  const preProcessingResult = await intakePdfPreProcessing(
    intakeFormId,
    appointmentId,
    consumerId,
    consumerInfo,
    formName,
  );
  if (!preProcessingResult || !consumerInfo.userId) {
    return null;
  }
  datadogLogs.logger.info(
    `Resubmitting ${formName} intake pdf to ehr for appointment '${appointmentId}'`,
    {
      appointmentId,
      formId: intakeFormId,
      orgId,
      formName,
      consumerId: consumerId,
    },
  );
  const {
    intakeFormQuestions,
    tempHiddenQuestions,
    defaultValues,
    sectionedQuestions,
    timeCreated,
  } = preProcessingResult;
  const pdfBlob = await generateIntakeFormPdfPC(
    consumerInfo,
    formName,
    "en" as LanguageCode,
    sectionedQuestions,
    defaultValues,
    timeCreated,
  );
  return createAndWriteIntakePdf(
    pdfBlob,
    formName,
    appointmentId,
    orgId,
    intakeFormId,
    consumerInfo,
    consumerInfo.userId,
    practiceId,
    true,
  );
};

export const generatePdf = async (
  intakeFormId: number,
  appointmentId: string,
  consumerId: string,
  consumerInfo: ConsumerData,
  isAdminView: boolean,
  formName: string,
) => {
  const preProcessingResult = await intakePdfPreProcessing(
    intakeFormId,
    appointmentId,
    consumerId,
    consumerInfo,
    formName,
  );
  if (!preProcessingResult) {
    return null;
  }
  const { defaultValues, sectionedQuestions, timeCreated } =
    preProcessingResult;

  return await generateIntakeFormPdfPC(
    consumerInfo,
    formName,
    "en" as LanguageCode,
    sectionedQuestions,
    defaultValues,
    timeCreated,
  );
};

const getIntakeFormResponse = async (
  appointmentId: string,
  intakeFormIds?: number[],
): Promise<IntakeFormSavedData[] | null> => {
  if (
    !appointmentId ||
    appointmentId.trim() === "" ||
    !intakeFormIds ||
    intakeFormIds.length === 0
  ) {
    return null;
  }

  const response = await fetch(
    `/api/intake/get-form-response-by-appointment?${new URLSearchParams({
      appointmentId: appointmentId,
      intakeFormIds: intakeFormIds?.join(","),
    })}`,
  );

  if (!response.ok) {
    return null;
  }

  const responseData = await response.json();

  if (responseData.responseStatus === "ERROR") {
    datadogLogs.logger.info("Error fetching intake forms by IDs", {
      appointmentId,
      intakeFormIds,
    });
    return null;
  }

  return responseData;
};

export const getIntakeFormsByOrgId = async (orgId: number) => {
  if (!orgId) {
    return null;
  }

  const response = await fetch(
    `/api/intake/get-forms-by-org-id?${new URLSearchParams({
      orgId: orgId.toString(),
    })}`,
  );

  if (!response.ok) {
    datadogLogs.logger.info("Error fetching intake forms by IDs", {
      orgId,
    });
    return null;
  }

  const responseData = await response.json();

  if (responseData.responseStatus === "ERROR") {
    return null;
  }

  return responseData;
};

export const getIntakeFormsByFormIds = async (
  intakeFormIds: number[],
  includeQuestions = false,
) => {
  if (!intakeFormIds || intakeFormIds.length === 0) {
    return null;
  }

  const response = await fetch(
    `/api/intake/get-forms-by-ids?${new URLSearchParams({
      intakeFormIds: intakeFormIds.join(","),
      includeQuestions: includeQuestions ? "true" : "false",
    })}`,
  );

  if (!response.ok) {
    datadogLogs.logger.info("Error fetching intake forms by IDs", {
      intakeFormIds,
      includeQuestions,
    });
    return null;
  }

  const responseData = await response.json();

  if (responseData.responseStatus === "ERROR") {
    return null;
  }

  return responseData;
};

const formatIntakeFormsWithQuestions = (forms, locale) => {
  return forms.map((form) => ({
    ...form,
    intakeFormToQuestions: translateIntakeForm(
      form.intakeFormToQuestions,
      locale ?? LanguageCode.En,
    )
      .map((question) => ({
        ...question,
        intakeQuestion: {
          ...question.intakeQuestion,
          questionAttributes: {
            ...question.intakeQuestion.questionAttributes,
            choices: question.intakeQuestion.questionAttributes.choices?.map(
              (c, index) => ({
                ...c,
                id: index.toString(),
              }),
            ),
          },
        },
      }))
      .sort((a, b) => a.questionNumber - b.questionNumber),
  }));
};
